// The desktop apps that host this web build and go live from their own
// native Studio window instead of the browser engine (seamless camera and
// microphone switching):
//
//   EventLive for Mac      window.EventLiveMac       "eventlivemac" events
//                          (macos/EventLive/Web/NativeBridge.swift)
//   EventLive for Windows  window.EventLiveWindows   "eventlivewindows" events
//                          (windows/EventLive/Web/NativeBridge.cs)
//
// Both bridges take the same calls — window.<bridge>.call(type, payload) ->
// Promise for openStudio, updateStudioEvent, getStudioState, getInfo and
// openExternal — and send the same window CustomEvents ({ type, streamKey,
// ... }): studioStatus and studioClosed. EventLive for Windows has a Studio
// only when its bridge says `studio: true`; without one it streams from "This
// Browser" (WebView2 is Edge), so the page treats it like a browser.
//
// The Mac-only helpers in components/macos/macApp.web.js predate this file;
// for the Mac this sends exactly the same calls (__tests__/desktopApp.test.js).
import { useEffect, useRef } from "react";
import { makeUrl, eventTitle } from "@/components/globalUtils";
import { isMacApp } from "@/components/macos/macApp";
import { getLanguage, t } from "@/localization";
import { logUploadToken } from "../../utils/logUploadToken";

const RTMPS_SERVER = "rtmps://go.eventlive.pro:443/live";

// Running inside EventLive for Windows, with or without its Studio.
export const isWindowsApp = () =>
  typeof window !== "undefined" &&
  Boolean(window.EventLiveWindows?.isWindowsApp);

// The desktop app whose Studio this page drives, or null in a browser, on a
// phone and in EventLive for Windows without a Studio.
function studioApp() {
  if (isMacApp()) {
    return {
      platform: "mac",
      bridge: window.EventLiveMac,
      eventName: "eventlivemac",
    };
  }
  if (isWindowsApp() && window.EventLiveWindows.studio === true) {
    return {
      platform: "windows",
      bridge: window.EventLiveWindows,
      eventName: "eventlivewindows",
    };
  }
  return null;
}

// "mac" (EventLive for Mac), "windows" (EventLive for Windows with its
// Studio) or null. Also the value of the Studio option in the go-live choices.
export const desktopStudioPlatform = () => studioApp()?.platform || null;

export const hasDesktopStudio = () => studioApp() !== null;

// The Studio option in the go-live choices: "This Mac" or "This PC".
export function desktopStudioLabel({ recommended = false } = {}) {
  switch (desktopStudioPlatform()) {
    case "mac":
      return recommended ? t("This Mac (Recommended)") : t("This Mac");
    case "windows":
      return recommended ? t("This PC (Recommended)") : t("This PC");
    default:
      return null;
  }
}

// Same fields the phone apps get from openStreamWindow (components/Utils.js).
const studioParams = (event, customDomain) => ({
  streamKey: event.stream_key,
  rtmpurl: `${RTMPS_SERVER}/${event.stream_key}`,
  title: eventTitle(event) || "",
  is_test: Boolean(event.is_test),
  url: makeUrl(event, customDomain),
  slug: event.slug,
  broadcaster: event.broadcaster,
  event_status: event.event_status,
  liveViewers: parseInt(event.liveViewers, 10) || 0,
  // The Studio shows its own words in the app's language ("de", "es-MX"…);
  // apps from before this ignore it.
  language: getLanguage(),
});

// Opens (or brings forward) the Studio for this event. Resolves true when
// the Studio is open, false without a desktop Studio or when the app refused
// because another event is already live (it tells the user itself).
export async function openDesktopStudio(event, customDomain) {
  const app = studioApp();
  if (!app || !event?.stream_key) return false;
  try {
    // The app sends it with the session log when the Studio closes
    // (docs/app-log-uploads.md); left out when signed out.
    const token = await logUploadToken();
    await app.bridge.call("openStudio", {
      ...studioParams(event, customDomain),
      ...(token ? { logUploadToken: token } : {}),
    });
    return true;
  } catch (error) {
    if (String(error?.message || error).includes("another_event_live"))
      return false;
    throw error;
  }
}

// Keeps the Studio's title/viewer count/status current from the event
// subscription the page already has.
export function updateDesktopStudioEvent(event) {
  const app = studioApp();
  if (!app || !event?.stream_key) return;
  app.bridge
    .call("updateStudioEvent", {
      streamKey: event.stream_key,
      title: eventTitle(event),
      event_status: event.event_status,
      liveViewers: parseInt(event.liveViewers, 10) || 0,
    })
    .catch(() => {});
}

// Subscribes to Studio events (studioStatus, studioClosed). Returns an
// unsubscribe function.
export function onDesktopStudioEvent(handler) {
  const app = studioApp();
  if (!app) return () => {};
  const listener = (e) => handler(e.detail || {});
  window.addEventListener(app.eventName, listener);
  return () => window.removeEventListener(app.eventName, listener);
}

// Calls `onClosed(detail)` when the Studio window for `streamKey` closes —
// the desktop counterpart of the phone apps' "StreamDone" event.
// detail.wentLive is true when the stream actually went live.
export function useDesktopStudioClosed(streamKey, onClosed) {
  const handlerRef = useRef(onClosed);
  handlerRef.current = onClosed;
  useEffect(() => {
    if (!hasDesktopStudio() || !streamKey) return undefined;
    return onDesktopStudioEvent((detail) => {
      if (detail.type === "studioClosed" && detail.streamKey === streamKey) {
        handlerRef.current?.(detail);
      }
    });
  }, [streamKey]);
}
